import type { RestaurantEnv } from "@dashboard/types";

export type WorkspaceOption = {
  uuid: string;
  name: string;
  env: RestaurantEnv;
  city: string | undefined;
  isRecentlyViewed: boolean;
};

export type WorkspaceSection = {
  key: string;
  heading: string;
  options: WorkspaceOption[];
};

export type WorkspaceSections = {
  current: WorkspaceOption | undefined;
  sections: WorkspaceSection[];
  flatOptions: WorkspaceOption[];
};

const MAX_RECENTS = 5;

// The current workspace is excluded; recents keep the server's view-history order.
export function buildWorkspaceSections({
  options,
  currentWorkspaceUuid,
}: {
  options: WorkspaceOption[];
  currentWorkspaceUuid: string | undefined;
}): WorkspaceSections {
  const current = options.find(
    (option) => option.uuid === currentWorkspaceUuid
  );

  const selectable = options.filter(
    (option) => option.uuid !== currentWorkspaceUuid
  );

  const recents = selectable
    .filter((option) => option.isRecentlyViewed)
    .slice(0, MAX_RECENTS);
  const recentUuids = new Set(recents.map((option) => option.uuid));

  const rest = selectable.filter((option) => !recentUuids.has(option.uuid));

  const sections: WorkspaceSection[] = [];
  if (recents.length > 0) {
    sections.push({ key: "recent", heading: "Recent", options: recents });
  }
  if (rest.length > 0) {
    sections.push({
      key: "all",
      heading: "All workspaces",
      options: rest,
    });
  }

  return { current, sections, flatOptions: [...recents, ...rest] };
}
